import * as React from "react";

import { ColorBadge } from "@/components/ui/color-badge";
import { H3 } from "@/components/headings";
import {
  convertToReadableContentFormat,
  getFirstConentTypeFromContentObject,
} from "@/lib/oas-tools/oas-schema-utils";
import { OASComponentsObject } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ReactNode } from "@tanstack/react-router";

export const DocumentationCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-card-foreground shadow", className)}
    {...props}
  />
));
DocumentationCard.displayName = "Card";

export const DocumentationCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    btnGroup?: ReactNode;
  }
>(({ className, children, btnGroup, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "flex pb-2 justify-between items-end border-b pl-4 pr-4",
        className
      )}
      {...props}
    >
      {children}
      {btnGroup}
    </div>
  );
});
DocumentationCardHeader.displayName = "CardHeader";

export const DocumentationCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <H3
    ref={ref}
    className={cn("text-sm font-normal text-muted-foreground", className)}
    {...props}
  />
));
DocumentationCardTitle.displayName = "CardTitle";

export const DocumentationCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("pt-1  max-w-screen-md]", className)}
    {...props}
  />
));
DocumentationCardDescription.displayName = "CardDescription";

export const DocumentationCardContent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn("pt-0", className)} {...props} />
));
DocumentationCardContent.displayName = "CardContent";

export const DocumentationCardItem = React.forwardRef<
  HTMLLIElement,
  React.HTMLAttributes<HTMLLIElement> & {
    isLast?: boolean;
    isSelected?: boolean;
  }
>(({ children, className, isSelected, isLast = false, ...props }, ref) => {
  const canHover = props.onClick;
  return (
    <li
      ref={ref}
      data-islast={isLast}
      className={cn(
        "group border-b data-[islast=true]:border-none py-0.5",
        className
      )}
      {...props}
    >
      <div
        aria-selected={isSelected}
        className={cn("py-0.5  rounded-md aria-selected:bg-accent mx-1 px-3", {
          "hover:bg-accent cursor-pointer transition-colors": canHover,
        })}
      >
        {children}
      </div>
    </li>
  );
});

export const DocumentationCardItemHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("flex justify-between items-center", className)}
      {...props}
    />
  );
});

export const DocumentationCardItemTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("", className)} {...props}>
      <H3 className="text-primary font-semibold">{children}</H3>
    </div>
  );
});

export const DocumentationCardButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...rest }, ref) => {
  return (
    <div
      className={cn("flex gap-1 items-center px-4", className)}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

export const DocumentationCardItemButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...rest }, ref) => {
  return (
    <div
      className={cn(
        "flex gap-1 items-center opacity-0 group-hover:opacity-100 transition-opacity",
        className
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  );
});

export const DocumentationCardItemContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return <div ref={ref} className={cn("pt-1", className)} {...props} />;
});

export const DocumentationCardContentBadge = ({
  contentObject,
  ...rest
}: React.ComponentProps<typeof ColorBadge> & {
  contentObject: OASComponentsObject | undefined;
}) => {
  const contentType =
    contentObject && getFirstConentTypeFromContentObject(contentObject);
  return (
    <ColorBadge color="ghost" {...rest}>
      {contentType ? convertToReadableContentFormat(contentType) : "unknown"}
    </ColorBadge>
  );
};
